import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import documentService from './documentService';

// Initial state
const initialState = {
  documents: [],
  claimDocuments: [],
  currentDocument: null,
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  isSuccess: false,
  isError: false,
  message: '',
  uploadSuccess: false,
  lastRefresh: 0, // Add timestamp to trigger refreshes
};

// Upload document for claim
export const uploadDocument = createAsyncThunk(
  'documents/upload',
  async ({ claimId, file, documentType }, thunkAPI) => {
    try {
      return await documentService.uploadDocument(claimId, file, documentType);
    } catch (error) {
      const message = error.message || 'Failed to upload document';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get documents by claim ID
export const getDocumentsByClaimId = createAsyncThunk(
  'documents/getByClaimId',
  async (claimId) => {
    try {
      const result = await documentService.getDocumentsByClaimId(claimId);
      // If result is undefined or null, return empty array
      if (!result) return [];
      
      // Extract documents array from API response structure
      // API returns { claimId, documents, totalCount }
      if (result.documents && Array.isArray(result.documents)) {
        return result.documents;
      }
      
      // If result is already an array, return it
      if (Array.isArray(result)) {
        return result;
      }
      
      return [];
    } catch {
      return [];
    }
  }
);

// Delete document
export const deleteDocument = createAsyncThunk(
  'documents/delete',
  async (documentId, thunkAPI) => {
    try {
      await documentService.deleteDocument(documentId);
      return documentId;
    } catch (error) {
      const message = error.message || 'Failed to delete document';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Download document
export const downloadDocument = createAsyncThunk(
  'documents/download',
  async ({ documentId, filename }, thunkAPI) => {
    try {
      const { data, fileName } = await documentService.downloadDocument(documentId);
      
      // Create blob and download
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || filename || `document-${documentId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { documentId, filename: fileName };
    } catch (error) {
      const message = error.message || 'Failed to download document';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get all documents
export const getAllDocuments = createAsyncThunk(
  'documents/getAll',
  async (_, thunkAPI) => {
    try {
      return await documentService.getAllDocuments();
    } catch (error) {
      const message = error.message || 'Failed to fetch all documents';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get document by ID for preview
export const getDocumentById = createAsyncThunk(
  'documents/getById',
  async (documentId, thunkAPI) => {
    try {
      return await documentService.getDocumentById(documentId);
    } catch (error) {
      const message = error.message || 'Failed to fetch document';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Validate document stamp
export const validateDocumentStamp = createAsyncThunk(
  'documents/validateStamp',
  async ({ documentId, isValid, notes }, { rejectWithValue }) => {
    try {
      // Console log removed for production
      const response = await documentService.validateDocumentStamp(documentId, isValid, notes);
      // Console log removed for production
      return response;
    } catch (error) {
      // Console log removed for production
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Override stamp validation (Approver only)
export const overrideStampValidation = createAsyncThunk(
  'documents/overrideStampValidation',
  async ({ documentId, isValid, notes }, { rejectWithValue }) => {
    try {
      // Console log removed for production
      const response = await documentService.overrideStampValidation(documentId, isValid, notes);
      // Console log removed for production
      return response;
    } catch (error) {
      // Console log removed for production
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Document slice
export const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    resetDocumentState: (state) => {
      state.isLoading = false;
      state.isUploading = false;
      state.uploadProgress = 0;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
      state.uploadSuccess = false;
    },
    clearDocumentMessage: (state) => {
      state.message = '';
      state.isError = false;
      state.isSuccess = false;
      state.uploadSuccess = false;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    // Add reducer to manually trigger refresh
    triggerRefresh: (state) => {
      state.lastRefresh = Date.now();
    },
    // Add reducer to clear upload state when starting new upload
    clearUploadState: (state) => {
      state.uploadSuccess = false;
      state.isError = false;
      state.message = '';
      state.uploadProgress = 0;
    },
    clearClaimDocuments: (state) => {
      state.claimDocuments = [];
    },
    setCurrentDocument: (state, action) => {
      state.currentDocument = action.payload;
    },
    clearCurrentDocument: (state) => {
      state.currentDocument = null;
    },
    setClaimDocuments: (state, action) => {
      state.claimDocuments = action.payload || [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.isUploading = true;
        state.uploadProgress = 0;
        state.uploadSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.isUploading = false;
        state.uploadSuccess = true;
        state.uploadProgress = 100;
        state.message = 'Document uploaded successfully';
        
        // Add the uploaded document to the list
        if (action.payload && action.payload.successfulUploads && action.payload.successfulUploads.length > 0) {
          state.claimDocuments.push(action.payload.successfulUploads[0]);
        }
        
        // Update lastRefresh to trigger list refresh
        state.lastRefresh = Date.now();
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isUploading = false;
        state.uploadSuccess = false;
        state.isError = true;
        state.message = action.payload || 'Failed to upload document';
        state.uploadProgress = 0;
        // Do not update lastRefresh on failure
      })
      
      // Get documents by claim ID
      .addCase(getDocumentsByClaimId.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(getDocumentsByClaimId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Ensure we always treat the payload as an array
        const documents = Array.isArray(action.payload) ? action.payload : [];
        state.documents = documents;
        state.claimDocuments = documents; // Update claimDocuments for the DocumentsSection
      })
      .addCase(getDocumentsByClaimId.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || 'Failed to fetch documents';
      })
      
      // Delete document
      .addCase(deleteDocument.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.claimDocuments = state.claimDocuments.filter(doc => doc.id !== action.payload);
        state.documents = state.documents.filter(doc => doc.id !== action.payload);
        state.message = 'Document deleted successfully';
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Download document
      .addCase(downloadDocument.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(downloadDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = `Downloaded ${action.payload.filename}`;
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Get all documents
      .addCase(getAllDocuments.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(getAllDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Extract documents array from API response structure
        state.documents = action.payload.documents || action.payload || [];
      })
      .addCase(getAllDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Get document by ID
      .addCase(getDocumentById.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(getDocumentById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.currentDocument = action.payload;
        state.message = '';
      })
      .addCase(getDocumentById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Validate document stamp
      .addCase(validateDocumentStamp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(validateDocumentStamp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.message = 'Stamp validation completed successfully';
        state.lastRefresh = Date.now();
        
        // Update the document in both arrays
        const updatedDocument = action.payload.document;
        if (updatedDocument) {
          // Update documents array
          const index = state.documents.findIndex(doc => doc.id === updatedDocument.id);
          if (index !== -1) {
            state.documents[index] = updatedDocument;
          }
          
          // Update claimDocuments array
          const claimIndex = state.claimDocuments.findIndex(doc => doc.id === updatedDocument.id);
          if (claimIndex !== -1) {
            state.claimDocuments[claimIndex] = updatedDocument;
          }
        }
      })
      .addCase(validateDocumentStamp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || 'Failed to validate stamp';
      })
      
      // Override stamp validation
      .addCase(overrideStampValidation.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(overrideStampValidation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.message = 'Stamp validation override completed successfully';
        state.lastRefresh = Date.now();
        
        // Update the document in both arrays
        const updatedDocument = action.payload.document;
        if (updatedDocument) {
          // Update documents array
          const index = state.documents.findIndex(doc => doc.id === updatedDocument.id);
          if (index !== -1) {
            state.documents[index] = updatedDocument;
          }
          
          // Update claimDocuments array
          const claimIndex = state.claimDocuments.findIndex(doc => doc.id === updatedDocument.id);
          if (claimIndex !== -1) {
            state.claimDocuments[claimIndex] = updatedDocument;
          }
        }
      })
      .addCase(overrideStampValidation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || 'Failed to override stamp validation';
      });
  },
});

export const { 
  resetDocumentState, 
  clearDocumentMessage, 
  setUploadProgress, 
  triggerRefresh,
  clearUploadState,
  clearClaimDocuments,
  setCurrentDocument,
  clearCurrentDocument,
  setClaimDocuments
} = documentSlice.actions;

export default documentSlice.reducer;

// Selectors
export const selectDocuments = (state) => state.documents.documents;
export const selectClaimDocuments = (state) => state.documents.claimDocuments;
export const selectCurrentDocument = (state) => state.documents.currentDocument;
export const selectDocumentsLoading = (state) => state.documents.isLoading;
export const selectDocumentsUploading = (state) => state.documents.isUploading;
export const selectUploadProgress = (state) => state.documents.uploadProgress;
export const selectDocumentsSuccess = (state) => state.documents.isSuccess;
export const selectDocumentsError = (state) => state.documents.isError;
export const selectDocumentsMessage = (state) => state.documents.message;
export const selectUploadSuccess = (state) => state.documents.uploadSuccess;
export const selectLastRefresh = (state) => state.documents.lastRefresh;
